import { useCallback, useMemo, useState } from "react";
import { TaskGroup, TaskGroupTaskCount } from "@common/types/taskGroup";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useDeleteWorkspaceTaskGroupMutation } from "@api/taskGroup.api";
import { useDeleteOrganizationTaskGroupMutation } from "@api/organizationTaskGroup.api";
import { TaskGroupFormModal } from "./TaskGroupFormModal";
import {
    WorkspaceTaskGroupColumn,
    WorkspaceTaskGroupRow,
    workspaceTaskGroupsViewConfig,
} from "./workspaceTaskGroupsViewConfig";

interface Props {
    search: string;
    canManageTaskGroups: boolean;
    taskGroups: TaskGroup[] | undefined;
    isTaskGroupsFetched: boolean;
    counts: TaskGroupTaskCount[] | undefined;
    isCountsFetched: boolean;
    scope: "workspace" | "organization";
}

interface WorkspaceTaskGroupsTableCallbacks {
    onEditTaskGroup: (taskGroupId: string) => void;
    onDeleteTaskGroup: (taskGroupId: string) => void;
}

interface WorkspaceTaskGroupsTableOptions {
    scope: "workspace" | "organization";
}

const defaultView: View = {
    type: ViewType.TASK_GROUP,
    columns: [
        { id: WorkspaceTaskGroupColumn.NAME },
        { id: WorkspaceTaskGroupColumn.TASKS },
        { id: WorkspaceTaskGroupColumn.UPDATED },
        { id: WorkspaceTaskGroupColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

export const TaskGroupsListBase = ({
    search,
    canManageTaskGroups,
    taskGroups,
    isTaskGroupsFetched,
    counts,
    isCountsFetched,
    scope,
}: Props) => {
    const { error: errorToast } = useToaster();

    const [editingTaskGroup, setEditingTaskGroup] = useState<TaskGroup | null>(
        null,
    );
    const [deletingTaskGroup, setDeletingTaskGroup] =
        useState<TaskGroup | null>(null);

    const deleteWorkspaceTaskGroupMutation =
        useDeleteWorkspaceTaskGroupMutation(deletingTaskGroup ?? undefined);
    const deleteOrganizationTaskGroupMutation =
        useDeleteOrganizationTaskGroupMutation(deletingTaskGroup ?? undefined);
    const deleteTaskGroupMutation =
        scope === "workspace"
            ? deleteWorkspaceTaskGroupMutation
            : deleteOrganizationTaskGroupMutation;

    const countsByTaskGroupId = useMemo(
        () =>
            new Map(
                (counts ?? []).map((taskGroupCount) => [
                    taskGroupCount.taskGroupId,
                    taskGroupCount.count,
                ]),
            ),
        [counts],
    );

    const taskGroupsList = useMemo(() => taskGroups ?? [], [taskGroups]);
    const taskGroupsById = useMemo(
        () =>
            new Map(
                taskGroupsList.map((taskGroup) => [taskGroup.id, taskGroup]),
            ),
        [taskGroupsList],
    );

    const filteredTaskGroups = useMemo(() => {
        if (!search) {
            return taskGroupsList;
        }

        const lowerCasedSearch = search.toLowerCase();
        return taskGroupsList.filter((taskGroup) =>
            taskGroup.name.toLowerCase().includes(lowerCasedSearch),
        );
    }, [search, taskGroupsList]);

    const rows = useMemo<WorkspaceTaskGroupRow[]>(
        () =>
            filteredTaskGroups.map((taskGroup) => ({
                id: taskGroup.id,
                name: taskGroup.name,
                updatedAt: taskGroup.updatedAt,
                organizationId: taskGroup.organizationId,
                taskCount: isCountsFetched
                    ? (countsByTaskGroupId.get(taskGroup.id) ?? 0)
                    : null,
                systemKey: taskGroup.systemKey,
            })),
        [countsByTaskGroupId, filteredTaskGroups, isCountsFetched],
    );

    const handleEditTaskGroup = useCallback(
        (taskGroupId: string) => {
            const taskGroup = taskGroupsById.get(taskGroupId);
            if (taskGroup) {
                setEditingTaskGroup(taskGroup);
            }
        },
        [taskGroupsById],
    );

    const handleDeleteTaskGroup = useCallback(
        (taskGroupId: string) => {
            const taskGroup = taskGroupsById.get(taskGroupId);
            if (taskGroup) {
                setDeletingTaskGroup(taskGroup);
            }
        },
        [taskGroupsById],
    );

    const tableMeta = useMemo<
        ViewTableMeta<
            WorkspaceTaskGroupsTableCallbacks,
            WorkspaceTaskGroupsTableOptions
        >
    >(
        () => ({
            callbacks: canManageTaskGroups
                ? {
                      onEditTaskGroup: handleEditTaskGroup,
                      onDeleteTaskGroup: handleDeleteTaskGroup,
                  }
                : undefined,
            options: {
                scope,
            },
        }),
        [
            canManageTaskGroups,
            scope,
            handleDeleteTaskGroup,
            handleEditTaskGroup,
        ],
    );

    return (
        <>
            <ViewTable
                data={isTaskGroupsFetched ? rows : null}
                view={defaultView}
                config={workspaceTaskGroupsViewConfig}
                emptyView={() => <EmptyState header="No task groups found" />}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                enableColumnResizing={false}
                outerBorder
            />

            <TaskGroupFormModal
                show={!!editingTaskGroup}
                taskGroup={editingTaskGroup ?? undefined}
                onHide={() => setEditingTaskGroup(null)}
                scope={scope}
            />

            <ConfirmationModal
                show={!!deletingTaskGroup}
                title={`Delete ${deletingTaskGroup?.name}?`}
                question="This task group will be removed."
                yes="Delete"
                no="Cancel"
                onReject={() => setDeletingTaskGroup(null)}
                onConfirm={async () => {
                    try {
                        await deleteTaskGroupMutation.mutateAsync();
                        setDeletingTaskGroup(null);
                    } catch {
                        errorToast("Failed to delete task group");
                    }
                }}
            />
        </>
    );
};
